<template>
  <AsyncLoader v-if="loadingPermissions"></AsyncLoader>
  <collapsible-section
    v-else
    class='_pt0 _pb0 -stretch-actions'
  >
    <template #headerText>
      <p>{{ title }}</p>
    </template>
    <template #headerActions>
      <app-search-input
        v-if="userHasGroups"
        label="Filter Groups"
        name="filterOn"
        v-model="filterOn"
      />
      <button
        @click="onAddGroup"
        v-if="!readonly && !ownProfile"
        class='btn white --sm _mrxs'
      >
        Add A Group
      </button>
      <button
        v-if="isCollapsed && userHasGroups"
        @click="toggleAll(false)"
        class='btn white --sm _mrxs'
      >
        Open All
      </button>
      <button
        v-else-if="userHasGroups"
        @click="toggleAll(true)"
        class='btn white --sm _mrxs'
      >
        Collapse All
      </button>
    </template>
    <template #collapsibleSection>
      <div container>
        <cloud-cannon-content
          v-if="!readonly"
          class="_p0 bg-light-gray"
          :content="cloudCannonContent.administrator_permissions"
          :content-loading="cloudCannonContentLoading"
        />
        <div
          v-if="!readonly"
          class="acl-table"
        >
          <table class="acl-table-header">
            <tr class="group-header-row">
              <td>
                <div class="cell empty-left"></div>
              </td>
              <td
                v-for="(perm, idx) in perms"
                :key="`${perm.name}${idx}`"
              >
                <div class="cell">
                  <div class="frm-toggle">
                    <div class="fakeLabel">
                      {{ perm.text }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="empty-right"></td>
            </tr>
          </table>
        </div>
        <app-acl-table
          v-for="(group, index) in filteredGroupsWithSubGroups"
          :key="`group${index}`"
          :group="group"
          @acl-change="onAclChange"
          @remove-group="onRemoveGroup"
          :collapsed="isCollapsed"
          :readonly="readonly"
        />
        <button
          class="btn white w-100 --sm  _mbs"
          @click="visibleRows += step"
          v-if="visibleRows < groupsWithSubGroups.length"
        >Load More</button>
      </div>
    </template>
  </collapsible-section>
</template>

<script>
import { computed, onBeforeMount, ref } from '@vue/composition-api';
import { useRoute, useRouter, useStore } from '@vue3shims';
import AppCheckbox from '@atoms/Checkbox';
import AppAclTable from '@organisms/v2/ACLTable';
import AppSearchInput from '@atoms/SearchInput';
import AsyncLoader from '@molecules/AsyncLoader';
import CollapsibleSection from '@sections/CollapsibleSection';
import { useHiddenRouteParams } from '@composables/useHiddenRouteParams';

export default {
  setup(props, { emit }) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { hiddenRouteParams } = useHiddenRouteParams();
    const activeUser = computed(() => store.getters['users2/activeUser']);
    const visibleRows = ref(3)
    const step = ref(3)
    const cloudCannonContent = computed(() => store.getters['cloudCannon/cloudCannonContent']);
    const cloudCannonContentLoading = computed(() => store.getters['cloudCannon/cloudCannonContentLoading']);
    const filteredGroupsWithSubGroups = computed(() => {
      return groupsWithSubGroups.value.sort((a, b) => a.groupName.localeCompare(b.groupName)).slice(0, visibleRows.value);
    });
    const perms = computed(() => {
      let rawPerms = store.getters['users2/perms'] || [];
      if (rawPerms.length > 0 && ['B', 'T'].includes(activeUser.value.accountType)) {
        rawPerms = rawPerms.filter((perm) => perm.name !== 'createGroupUsersAllowed');
      }
      return rawPerms;
    });
    const filterOn = ref('');
    const groupsWithSubGroups = computed(() => {
      if (store.getters['users2/activeUserRole']) {
        let rawGroups = store.getters['users2/activeUserRole'].groupsWithSubGroups || [];
        let filtered = rawGroups.filter((group) => {
          if (filterOn.value !== '') {
            return (
              group.groupName.toLowerCase().includes(filterOn.value.toLowerCase())
              || group.subGroupName.toLowerCase().includes(filterOn.value.toLowerCase())
              || group.groupId.toLowerCase().includes(filterOn.value.toLowerCase())
              || group.subGroupId.toLowerCase().includes(filterOn.value.toLowerCase())
            );
          }
          else {
            return true;
          }
        });
        // console.log(`groupsWithSubGroups`, filtered);
        return filtered;
      }
      return [];
    });
    const isCollapsed = ref(props.collapsed || true);
    const isInternalUser = computed(() => store.getters['isInternalUser']);
    const ownProfile = computed(() => store.getters['userId'] === hiddenRouteParams.value.userId);
    const userHasGroups = computed(() => groupsWithSubGroups.value.length > 0);
    const loadingPermissions = computed(() => store.getters['users2/loading']);

    const headerBoxChecked = (perm) => {
      let subsWithAccess = [];
      let totalSubGroups = 0;
      groupsWithSubGroups.value.forEach((group) => {
        group.subGroups.forEach((subGroup) => {
          totalSubGroups++;
          if (subGroup.access[perm] === true) {
            subsWithAccess.push(subGroup);
          }
        });
      });
      return subsWithAccess.length === totalSubGroups;
    }

    const onAclChange = (acl) => {
      let currentAccess = store.getters['users2/activeUserRole'].subGroupAccess;
      // console.log(currentAccess);
      let myGroups = JSON.parse(JSON.stringify(currentAccess));
      // one group, all perms, all subgroups
      if (acl.perm === '__ALL__' && acl.subGroupCk === '__ALL__') {
        myGroups.forEach((group) => {
          if (group.groupCk === acl.groupCk) {
            Object.keys(group.access).forEach((key) => {
              group.access[key] = acl.value;
            });
          }
        });
      }
      // all permissions, one group
      else if (acl.perm === '__ALL__') {
        myGroups.forEach((group) => {
          if (group.subGroupCk === acl.subGroupCk) {
            Object.keys(group.access).forEach((key) => {
              if (key !== 'primaryContact') {
                group.access[key] = acl.value;
              }
            });
          }
        });
      }
      // primaryContact
      else if (acl.perm === 'primaryContact') {
        myGroups.forEach((group) => {
          if (group.groupId === acl.groupId) {
            group.access.primaryContact = acl.value;
          }
        });
      }
      // all subgroups, one perm, possibly by group
      else if (acl.subGroupCk === '__ALL__') {
        myGroups.forEach((group) => {
          if (acl.groupCk !== '__ALL__') {
            if (group.groupCk === acl.groupCk) {
              group.access[acl.perm] = acl.value;
            }
          }
          else {
            group.access[acl.perm] = acl.value;
          }
        });
      }
      // one subgroup, one perm
      else {
        myGroups.forEach((group) => {
          if (group.groupCk === acl.groupCk && group.subGroupCk === acl.subGroupCk) {
            // console.log(`groupCk and subGroupCk match`);
            group.access[acl.perm] = acl.value;
            // console.log(group, group.access[acl.perm]);
          }
        });
      }
      emit('group-update', myGroups);
    }

    const onAddGroup = () => {
      store.commit('users2/CLEAR_GROUPS_TO_ADD');
      const userId = hiddenRouteParams.value.userId
      if (userId) {
        router.push({ name: 'user-add-sub-group', params: { userId: hiddenRouteParams.value.userId } });
      }
      else {
        router.push({ name: 'user-add-sub-group' });
      }
    }

    const onRemoveGroup = (removeCk) => {
      // console.log('onRemoveGroup', removeCk);
      let currentAccess = store.getters['users2/activeUserRole'].subGroupAccess;
      // console.log(currentAccess);
      let myGroups = JSON.parse(JSON.stringify(currentAccess));
      let filteredGroups = myGroups.filter((group) => group.groupCk !== removeCk);
      emit('group-update', filteredGroups);
    }

    

    const setAllSubGroups = (perm, value) => {
      onAclChange({ groupCk: '__ALL__', subGroupCk: '__ALL__', perm, value });
    }

    const toggleAll = (collapse) => {
      isCollapsed.value = collapse;
    }

    onBeforeMount(async () => {
      await store.dispatch('cloudCannon/GET_CLOUD_CANNON_CONTENT', { cloudCannonResource: '/user_add_edit_screen.json' });
    });


    return {
      hiddenRouteParams,
      cloudCannonContent,
      cloudCannonContentLoading,
      filterOn,
      groupsWithSubGroups,
      headerBoxChecked,
      isCollapsed,
      isInternalUser,
      loadingPermissions,
      ownProfile,
      onAclChange,
      onAddGroup,
      onRemoveGroup,
      perms,
      setAllSubGroups,
      toggleAll,
      userHasGroups,
      filteredGroupsWithSubGroups,
      visibleRows,
      step
    }
  },
  props: {
    collapsed: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    title: {
      type: String,
      required: false,
      default: () => 'Administrator Permissions',
    },
  },
  components: {
    AppAclTable,
    AppCheckbox,
    AppSearchInput,
    CollapsibleSection,
    AsyncLoader
  },
}
</script>

<style scoped>
.acl-table table tr.group-header-row td:first-child {
  padding: 0;
  width: initial;
}

@media screen and (min-width: 1100px) {
  .empty-left {
    min-width: initial !important;
    width: 22rem;
  }
}

.w-100{
  width: 100%;
}

.empty-left {
  min-width: 24rem;
}
.empty-right {
  min-width: 4rem;
}
</style>
